import ReactDOMServer from 'react-dom/server';
import React from 'react';
import PropTypes from 'prop-types';

import { useTranslation } from 'react-i18next';

import { makeStyles } from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import Table from '@material-ui/core/Table';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableRow from '@material-ui/core/TableRow';
import TableBody from '@material-ui/core/TableBody';
import TableHead from '@material-ui/core/TableHead';

import L from 'leaflet';
import {
  Marker, Popup, Tooltip,
} from 'react-leaflet';
import {
  WaterLevelYellowIcon, WaterLevelRedIcon, WaterLevelBlueIcon,
  WaterLevelGreenIcon, WaterLevelBrownIcon, WaterLevelGreyIcon,
} from '../../../assets/svg/Icon';

// components
import WaterLevelColor from '../../../components/color/waterlevel/WaterLevelColor';
import StyledMarker from '../../../components/marker/Marker';

// leaflet
import 'leaflet/dist/leaflet.css';

import '../../../components/map/Style.css';

import { styles } from '../style';

const useStyles = makeStyles(styles);
const styleMarkerWaterLevel = {
  type: 'waterlevel',
  level5: {
    icon: 'WaterLevelIcon',
    label: 'triangleRed',
  },
  level1: {
    icon: 'WaterLevelIcon',
    label: 'triangleYellow',
  },
};

export default function WaterLevelMap(props) {
  const { t } = useTranslation();
  const classes = useStyles();

  const { datas } = props;

  const formatWl = (value) => (typeof value === 'undefined' || value == null ? '-' : parseFloat(value).toFixed(2));

  const setWaterLevelStatus = (wl) => {
    let wlStationIcon = null;
    if (wl === null) {
      wlStationIcon = (<WaterLevelGreyIcon style={{ fontSize: '6rem' }} />);
    } else if (wl > 100) {
      wlStationIcon = (<WaterLevelRedIcon style={{ fontSize: '6rem' }} />);
    } else if (wl > 70) {
      wlStationIcon = (<WaterLevelBlueIcon style={{ fontSize: '6rem' }} />);
    } else if (wl > 30) {
      wlStationIcon = (<WaterLevelGreenIcon style={{ fontSize: '6rem' }} />);
    } else if (wl > 10) {
      wlStationIcon = (<WaterLevelYellowIcon style={{ fontSize: '6rem' }} />);
    } else if (wl <= 10) {
      wlStationIcon = (<WaterLevelBrownIcon style={{ fontSize: '6rem' }} />);
    } else {
      wlStationIcon = (<WaterLevelGreyIcon style={{ fontSize: '6rem' }} />);
    }
    return wlStationIcon;
  };

  const eachWaterLevelData = (data, i) => {
    if (data.tele_station_lat && data.tele_station_long
      && data.waterlevel_msl != null) {
      // marker position
      const position = [parseFloat(data.tele_station_lat),
      parseFloat(data.tele_station_long)];

      // status colors
      const statusColor = WaterLevelColor(data.storage_percent);

      let locationText = '';
      if (data.tumbon_name !== undefined) {
        locationText = `ต.${data.tumbon_name} `;
      }
      if (data.amphoe_name !== undefined) {
        locationText += `อ.${data.amphoe_name} `;
      }
      if (data.province_name !== undefined) {
        locationText += `จ.${data.province_name}`;
      }

      // marker icons
      const styleLevel = (data.level === 1)
        ? styleMarkerWaterLevel.level1 : styleMarkerWaterLevel.level5;
      const icon = L.divIcon({
        className: 'styleLevel',
        html: ReactDOMServer.renderToString(<StyledMarker
          category={styleMarkerWaterLevel.type}
          type={styleLevel}
        />),
      });

      return (
        <Marker
          id={data.id}
          key={i}
          position={position}
          icon={icon}
        >
          <Popup autoPan>
            <TableContainer>
              <Table size="small">
                <TableHead />
                <TableBody>
                  <TableRow>
                    <TableCell className={classes.hiddenLine} padding="none" align="center" colSpan={3}>
                      <Box fontWeight="500" fontFamily="Kanit" fontSize="14px">
                        {data.tele_station_name}
                      </Box>
                      <Box fontFamily="Kanit" fontSize="12px">
                        {locationText}
                      </Box>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className={classes.hiddenLine} padding="none" align="center" colSpan={3}>
                      <Box display="flex" justifyContent="center" alignItems="center">
                        <Box>
                          {setWaterLevelStatus(parseFloat(data.storage_percent))}
                        </Box>
                        <Box>
                          <Box
                            className={classes.statusValue}
                            color={statusColor.color.statusColor}
                          >
                            {parseFloat(data.storage_percent)}
                            %
                          </Box>
                          <Box
                            p={1}
                            className={classes.statusText}
                            fontFamily="Kanit"
                            bgcolor={statusColor.color.statusColor}
                            color={statusColor.color.constrastColor}
                          >
                            {statusColor.color.label}
                          </Box>
                        </Box>
                      </Box>
                      <Box mb={2} fontSize="12px" fontFamily="Kanit">
                        {t('ปริมาณความจุลำน้ำ')}
                      </Box>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell padding="none" align="center" colSpan={3} />
                  </TableRow>
                  <TableRow>
                    <TableCell className={classes.hiddenLine}>
                      <Box fontWeight={600} fontSize={12}>
                        {t('ระดับน้ำ')}
                      </Box>
                    </TableCell>
                    <TableCell className={classes.hiddenLine}>
                      {formatWl(data.waterlevel_msl)}
                    </TableCell>
                    <TableCell className={classes.hiddenLine} align="right">{t('ม.รทก.')}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className={classes.hiddenLine}>
                      <Box fontWeight={600} fontSize={12}>
                        {t('ระดับตลิ่ง')}
                      </Box>
                    </TableCell>
                    <TableCell className={classes.hiddenLine}>
                      {formatWl(data.min_bank)}
                    </TableCell>
                    <TableCell className={classes.hiddenLine} align="right">{t('ม.รทก.')}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className={classes.hiddenLine}>
                      <Box fontWeight={600} fontSize={12}>
                        {t('ระดับท้องน้ำ')}
                      </Box>
                    </TableCell>
                    <TableCell className={classes.hiddenLine}>
                      {formatWl(data.ground_level)}
                    </TableCell>
                    <TableCell className={classes.hiddenLine} align="right">{t('ม.รทก.')}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell padding="none" align="center" colSpan={3} />
                  </TableRow>
                  <TableRow>
                    <TableCell className={classes.hiddenLine} colSpan={3}>
                      <Box fontSize="10px">
                        {t('ข้อมูล')}
                        {' '}
                        :
                        {data.agency_name}
                      </Box>
                      <Box fontSize="10px">
                        {t('ล่าสุด')}
                        {' '}
                        :
                        {data.waterlevel_datetime}
                        {' '}
                        น.
                      </Box>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Popup>
          <Tooltip title={`สถานี${data.tele_station_name}`}>
            <span>
              <strong>
                {`สถานี${data.tele_station_name}`}
              </strong>
              <br />
              {`${t('ต.')}${data.tumbon_name} ${t('อ.')}${data.amphoe_name} `}
              <br />
              {`${t('จ.')}${data.province_name}`}
            </span>
          </Tooltip>
        </Marker>
      );
    }
    return false;
  };

  return (
    <>
      {datas.map(eachWaterLevelData)}
    </>
  );
}

WaterLevelMap.propTypes = {
  datas: PropTypes.instanceOf(Array).isRequired,
};
